import React, { useEffect, useRef } from 'react'
import { Avatar, Text, Touchable, View } from '@/components'
import { TextBubble, TextBubbleProps } from '../atoms/TextBubble'
import { AppIcon, variantProvider } from '@/app'
import { useBooleanToggle, useState } from '@codeleap/common'
import { Chapter, CommunityComment, CommunityFeed } from '@/types'
import { FeedDimensions } from '../FeedItems/shared'
import { TagType } from '../atoms/TagType'
import { FeedCommentDrawer } from '../CommentList'
import { feedIconsMap } from '../constants'
import { LessonReference } from '../atoms/LessonReference'

type FeedTextOverlayProps = TextBubbleProps & {
  comment?: CommunityComment
  icon?: AppIcon
  lesson?: Pick<Chapter, 'id' | 'title'>
}

const TITLE_LINES = 1
const DESCRIPTION_LINES = 3

function TextOverlay({ title, description, icon, lesson }: FeedTextOverlayProps) {
  const [showAll, toggle] = useBooleanToggle(false)
  const [overflowDetected, setOverflowDetected] = useState(false)

  const titleRef = useRef<HTMLDivElement>(null)
  const descRef = useRef<HTMLDivElement>(null)

  const buttonText = showAll ? 'Show Less' : 'Read More'

  useEffect(() => {
    requestAnimationFrame(() => {
      let titleOverflow = false
      let descOverflow = false

      if (titleRef.current) {
        const lineHeight = parseInt(getComputedStyle(titleRef.current).lineHeight) || 24
        titleOverflow = titleRef.current.scrollHeight > lineHeight * TITLE_LINES
      }

      if (descRef.current && description) {
        const lineHeight = parseInt(getComputedStyle(descRef.current).lineHeight) || 24
        descOverflow = descRef.current.scrollHeight > lineHeight * DESCRIPTION_LINES
      }

      setOverflowDetected(titleOverflow || descOverflow)
    })
  }, [])

  return (
    <View onClick={() => toggle()} css={[styles.noBubbleWrapper, { cursor: overflowDetected ? 'pointer' : 'auto' }]}>
      <View variants={['flex', 'gap:1', 'alignStart', 'column']}>
        <TagType icon={icon} />
        {title && (
          <Text
            variants={['h5', 'color:neutral1']}
            style={{
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: showAll ? undefined : TITLE_LINES,
              WebkitBoxOrient: 'vertical'
            }}
          >
            <p ref={titleRef}>{title}</p>
          </Text>
        )}
      </View>

      {description ? (
        <Text
          variants={['p3', 'color:neutral1']}
          style={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: showAll ? undefined : DESCRIPTION_LINES,
            WebkitBoxOrient: 'vertical'
          }}
        >
          <p ref={descRef}>{description}</p>
        </Text>
      ) : null}

      {overflowDetected ? <Text variants={['p2', 'color:primary2']} text={buttonText} /> : null}

      <LessonReference lesson={lesson} />
    </View>
  )
}

const CommentOverlay = (props: { comment: CommunityComment }) => {
  const { comment } = props

  return (
    <View
      variants={['gap:1', 'paddingLeft:2', 'paddingBottom:2']}
      onClick={() => FeedCommentDrawer.toggle()}
      style={{ zIndex: 10 }}
    >
      <Avatar
        debugName={`Feed item top comment`}
        image={comment.profile.avatar}
        name={comment.profile.display_name}
        variants={['alignSelfEnd', 'small']}
      />
      <View style={styles.commentTextWrapper}>
        <Text text={comment.text} variants={['feedCommentOverlay', 'color:neutral1']} style={styles.commentText} />
      </View>
    </View>
  )
}

export function FeedTextOverlay(props: FeedTextOverlayProps) {
  return (
    <View style={styles.wrapper}>
      {!!props?.comment && <CommentOverlay comment={props.comment} />}
      <TextOverlay {...props} />
    </View>
  )
}

FeedTextOverlay.extractProps = (item: CommunityFeed): FeedTextOverlayProps => {
  let title = null
  let description = null
  let lesson = null

  switch (item.type) {
    case 'lesson':
      title = item?.lesson?.title
      description = item?.lesson?.description
      break
    case 'image_answer':
      title = item?.prompt?.title
      description = item?.prompt?.description
      lesson = item?.post?.lesson
      break
    case 'video_answer':
      title = item?.prompt?.title
      description = item?.prompt?.description
      lesson = item?.post?.lesson
      break
    default:
      const bubble = TextBubble.extractProps(item)
      title = bubble?.title
      description = bubble?.description
      lesson = item?.post?.lesson
      break
  }

  const icon = (feedIconsMap[item.type] as AppIcon) || null

  return {
    title,
    description,
    comment: item.top_comment,
    icon,
    lesson
  }
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      ...theme.presets.fullWidth,
      ...theme.presets.absolute,
      ...theme.presets.bottom,
      ...theme.presets.column
    },
    noBubbleWrapper: {
      background: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.81))',
      ...theme.spacing.paddingLeft(2),
      ...theme.spacing.paddingRight(9),
      ...theme.spacing.paddingBottom(3),
      ...theme.spacing.paddingTop(2),
      ...theme.spacing.gap('3px'),
      ...theme.presets.column,
      ...theme.presets.alignStart,
      ...theme.presets.justifyCenter,
      zIndex: 5
    },
    commentText: {
      maxWidth: `calc(${FeedDimensions.WIDTH} * 0.5)`,
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 3,

      overflow: 'hidden',
      display: '-webkit-box',
      textOverflow: 'ellipsis',
      whiteSpace: 'normal'
    },
    commentTextWrapper: {
      backgroundColor: '#0000008C',
      padding: `10.36px 5.18px`,

      borderRadius: theme.borderRadius.tiny,
      borderBottomLeftRadius: 0
    }
  }),
  true
)
